import {
  $createMarkNode,
  $getMarkIDs,
  $isMarkNode,
  $unwrapMarkNode,
  MarkNode,
} from "@lexical/mark";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister, registerNestedElementResolver } from "@lexical/utils";
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
} from "lexical";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { cloneDeep } from "lodash";
import AnnoLightbox from "./AnnoLightbox";
import { AnnoOverlapPicker } from "./AnnoOverlapPicker";
import { useAnnoContextLex } from "./context";
import { useAnnoEditorRegistry } from "./annoEditorRegistry";
import { handleCreateAnnotation } from "./handlers/handleCreateAnnotation";
import { applyClassesForAnnoColor } from "./utils/applyClassesForAnnoColor";
import { constructAndInjectLexicalValueForEachAnnoDataId } from "./utils/constructAndInjectLexicalValueForEachAnnoDataId";
import { getAnnoLightboxPosition } from "./utils/getAnnoLightboxPosition";
import { useHighlightAnnoOnEvent } from "./utils/useHighlightAnnoOnEvent";
import { useDocCollaborationContext } from "@/contexts/docCollaborationContext";
import { useCollabFieldEngagementContext } from "@/contexts/collabFieldEngagementContext";

export const SHOW_ANNO_LIGHTBOX_COMMAND = createCommand(
  "SHOW_ANNO_LIGHTBOX_COMMAND"
);

const LOCKED_BY_OTHER_CLASS = "-locked-by-other-";

// TEMP / quick-and-dirty: which AnnoOverlapPicker UI to show when annotations
// overlap — "list" (full-text rows) or "badges" (lettered badges). Read live
// from localStorage (toggled in PublishedPageV3 View settings) at picker-open
// time, so no reactive wiring is needed. Defaults to "badges".
// Remove this + the View-settings toggle once we commit to one UI.
const OVERLAP_PICKER_LS_KEY = "anno_overlap_picker_variant";
const getOverlapPickerVariant = () =>
  typeof window !== "undefined" &&
  window.localStorage.getItem(OVERLAP_PICKER_LS_KEY) === "list"
    ? "list"
    : "badges";

/*

moved this markNodeMap outside the component so that it can be imported in the AnnoSuggestionsPlugin.

 best to context and share the state?.

*/

const editorMap = new WeakMap(); // to avoid memory leaks

export const getMarkNodeMap = (editor) => {
  if (!editorMap.has(editor)) {
    editorMap.set(editor, new Map());
  }

  return editorMap.get(editor);
};

export const AnnoPlugin = ({ annotation, readOnly, disableViewHighlights, allowAnnotationEditWhileReadOnly, v2, onAnnotationClick, activeAnnoIds, valuePath }) => {
  const [editor] = useLexicalComposerContext();
  const { handleSetAnnoData, annoData, handleUpdateFragmentsInAnnoData, latestAnnoDataRef, isAnnoMutatingRef } =
    useAnnoContextLex();
  const [showAnnoLightbox, setShowAnnoLightbox] = useState(undefined);

  // V2-only overlapping-annotation picker (Lightbox + List). Holds the anchor
  // mark element + the specificity-sorted annotation rows when open; undefined
  // when closed.
  const [overlapPicker, setOverlapPicker] = useState(undefined);
  // True while the overlap picker is open. Synced via ref so the long-lived
  // mark mouseover listener can read the current value and suppress hover
  // styling on the document marks while the picker is up.
  const overlapPickerOpenRef = useRef(false);
  overlapPickerOpenRef.current = !!overlapPicker;

  // Realtime collab state — undefined when no host has provided the context
  // (e.g. legacy edit pages). When present, the plugin paints a lock
  // indicator on marks held by other users and routes setEditMode through
  // an acquire-lock check.
  const docCollab = useDocCollaborationContext();

  // Collab field-lock engagement (null outside collab-editable fields).
  // The lightbox + overlap picker render in portals — DOM focus leaves the
  // field while the user is still working on it. Declaring engagement here
  // holds the field lock deterministically for the lifetime of either
  // popover; closing them resumes the normal blur/grace release flow.
  const collabFieldEngagement = useCollabFieldEngagementContext();
  const fieldToolingOpen = !!showAnnoLightbox || !!overlapPicker;
  useEffect(() => {
    if (!collabFieldEngagement) return undefined;
    // setEngaged is idempotent (transition-only) — safe to fire on every
    // change, incl. the initial false on mount.
    collabFieldEngagement.setEngaged(fieldToolingOpen);
    // unmount with tooling open (e.g. doc navigation) must not leave the lock pinned
    return () => collabFieldEngagement.setEngaged(false);
  }, [fieldToolingOpen, collabFieldEngagement]);

  // Tracks the annoDataId for which this client currently holds the collab
  // edit lock. Set when setEditMode succeeds in acquiring; cleared whenever
  // the lightbox closes (via any path).
  const heldLockDataIdRef = useRef(null);

  // Holds the text selection to annotate, captured the moment the create
  // lightbox opens (SHOW_ANNO_LIGHTBOX_COMMAND). Needed because the create
  // flow may involve typing into a new-tag text input, which collapses the
  // editor's selection before the mark gets wrapped — so we remember it here
  // and hand it to handleCreateAnnotation. Previously the AnnoLightbox
  // captured this itself via useLexicalComposerContext; that capture now lives
  // here so the lightbox can be rendered standalone (e.g. from an AnnoCard
  // with no editor in scope).
  const createTargetSelectionRef = useRef(null);

  // Set of annoDataIds currently filtered OUT (dimmed). Empty == no filter
  // active. Replaced wholesale by setDimmedAnnos; consumed by
  // repaintFilterHighlights to compute each fragment's included-vs-excluded split.
  const dimmedIdsRef = useRef(new Set());

  // The grey colour the host wants dimmed (filtered-out) marks tinted with.
  // Supplied via setDimmedAnnos's `color` arg because the shared plugin has no access
  // to a page-specific palette; it's typically a CSS var (e.g. V3's
  // var(--grey-10)) that resolves on the mark via the host theme root. We pass
  // it through to CSS as --anno-dim-color rather than baking a colour in here.
  const dimColorRef = useRef(null);

  // Recompute the filter paint for every mark fragment. For each fragment we
  // split the annotations covering it into included (not dimmed) and excluded
  // (dimmed), and hand the included count to CSS via --anno-active plus the
  // grey colour via --anno-dim-color (set only when this fragment has ≥1
  // excluded annotation) and the -anno-filtered- class. The stylesheet
  // composites a yellow layer (per included anno) over the grey layer, so
  // overlaps of included + excluded annotations read as a yellow/grey mix
  // rather than double-strength yellow. When the filter clears (no dimmed ids),
  // we strip the class + props so the normal --anno-depth colouring resumes.
  const repaintFilterHighlights = () => {
    const dimmed = dimmedIdsRef.current;
    const filterActive = dimmed.size > 0;
    const dimColor = dimColorRef.current;
    editor.getEditorState().read(() => {
      const seenKeys = new Set();
      for (const keys of markNodeMap.values()) {
        for (const key of keys) {
          if (seenKeys.has(key)) continue;
          seenKeys.add(key);
          const el = editor.getElementByKey(key);
          if (!el) continue;

          if (!filterActive) {
            el.classList.remove("-anno-filtered-");
            el.style.removeProperty("--anno-active");
            el.style.removeProperty("--anno-dim-color");
            continue;
          }

          const node = $getNodeByKey(key);
          const fragIds = node && $isMarkNode(node) ? node.getIDs() : [];
          let active = 0;
          let dim = 0;
          for (const fid of fragIds) {
            if (dimmed.has(fid)) dim += 1;
            else active += 1;
          }
          el.classList.add("-anno-filtered-");
          el.style.setProperty("--anno-active", String(active));
          // Grey layer only where something IS filtered out on this fragment;
          // a fully-included fragment stays pure yellow.
          if (dim > 0 && dimColor) {
            el.style.setProperty("--anno-dim-color", dimColor);
          } else {
            el.style.removeProperty("--anno-dim-color");
          }
        }
      }
    });
  };

  // Hover specificity: among the annotations covering a hovered fragment, pick
  // the most specific one = the annotation with the SHORTEST total span (fewest
  // characters across all of its fragments). This mirrors the reference design,
  // where hovering an overlap surfaces the innermost / most-specific annotation
  // instead of lighting up every overlapping one at once. Span length is summed
  // live from markNodeMap so it reflects the current text. Ties keep the first
  // id. Returns null for an empty set.
  const getMostSpecificAnnoId = (ids) => {
    if (!ids || ids.length === 0) return null;
    if (ids.length === 1) return ids[0];
    let winner = ids[0];
    let winnerLen = Infinity;
    editor.getEditorState().read(() => {
      for (const id of ids) {
        const keys = markNodeMap.get(id);
        if (!keys || keys.size === 0) continue;
        let len = 0;
        for (const key of keys) {
          const node = $getNodeByKey(key);
          if (node) len += node.getTextContent().length;
        }
        if (len < winnerLen) {
          winnerLen = len;
          winner = id;
        }
      }
    });
    return winner;
  };

  const releaseHeldLockIfAny = () => {
    if (!docCollab) return;
    const id = heldLockDataIdRef.current;
    if (id) {
      heldLockDataIdRef.current = null;
      docCollab.releaseLock(id);
    }
  };

  const markNodeMap = getMarkNodeMap(editor);

  /**
   * When the host enables annotation editing on a readOnly editor (e.g.
   * PublishedPage), keep the editor non-editable normally, but flip
   * editable=true while a lightbox is open so the create/edit/remove flows
   * (which all call editor.update) can mutate the tree. Same pattern as
   * DecoratorNodeShell uses around modals in edit mode.
   */
  useEffect(() => {
    if (!allowAnnotationEditWhileReadOnly) return;
    editor.setEditable(Boolean(showAnnoLightbox));
    return () => {
      // Restore non-editable on unmount/lightbox-close so a stray re-render
      // can't leave the editor in an editable state.
      editor.setEditable(false);
    };
  }, [allowAnnotationEditWhileReadOnly, showAnnoLightbox, editor]);

  /**
   * Paint / unpaint the "locked by another user" indicator on every <mark>
   * element corresponding to an annoDataId currently held by someone else.
   * The CSS pulse + outline come from the .-locked-by-other- class; the
   * attribution tab text comes from the data-editing-by attribute (read
   * via attr() in the ::before rule). Re-runs whenever the locks map
   * changes. Mirrors useHighlightAnnoOnEvent for hover/select states.
   */
  useEffect(() => {
    if (!docCollab) return undefined;
    const locks = docCollab.locks || {};
    const elementsPainted = [];
    Object.keys(locks).forEach((annoDataId) => {
      if (!docCollab.isLockedByOther(annoDataId)) return;
      const keys = markNodeMap.get(annoDataId);
      if (!keys) return;
      const userName = locks[annoDataId]?.userName || "Someone";
      keys.forEach((nodeKey) => {
        const elem = editor.getElementByKey(nodeKey);
        if (elem) {
          elem.classList.add(LOCKED_BY_OTHER_CLASS);
          elem.setAttribute("data-editing-by", `${userName} is editing this annotation`);
          elementsPainted.push(elem);
        }
      });
    });
    return () => {
      elementsPainted.forEach((elem) => {
        elem.classList.remove(LOCKED_BY_OTHER_CLASS);
        elem.removeAttribute("data-editing-by");
      });
    };
  }, [docCollab?.locks, editor, markNodeMap]);

  /**
   * ---- SHARED (both modes) ----
   * Sets up the highlight hooks. Both are used in readOnly and edit mode.
   * - "select" hook: adds `-selected-` class (blue highlight) + opens lightbox
   * - "hover" hook: adds `-hovered-` class on mouseover
   *
   * What DRIVES these hooks differs by mode:
   * - EDIT MODE:  registerUpdateListener detects cursor position inside MarkNodes
   * - READONLY:   document click listener detects clicks on <mark> elements
   */
  const { activeAnnoDataIDs: selectedAnnoDataIDs, setActiveAnnoDataIDs, resetActiveAnnoDataIDs } =
    useHighlightAnnoOnEvent({
      hover_select: "select",
      classToApply: "-selected-",
      markNodeMap,
      annoData,
      editor,
      // Don't show lightbox on click when view highlights is disabled
      eventBasedCallback: disableViewHighlights ? {} : {
        select: ({ editor, activeData_with_relatedNodeKeys }) => {
          // V2 overlap is handled directly in the readOnly click listener below
          // — it opens the picker WITHOUT painting any select/hover styling on
          // the document marks. What remains here: host single-click → focus the
          // card, and the in-editor display lightbox.

          // When a host provides onAnnotationClick, defer to it (scroll + focus
          // the corresponding card) instead of opening the in-editor lightbox.
          // On a non-V2 overlap this picks the first id — the original
          // pre-chooser behaviour.
          if (onAnnotationClick) {
            setOverlapPicker(undefined);
            const id = activeData_with_relatedNodeKeys?.[0]?.dataId;
            if (id) onAnnotationClick(id);
            return;
          }
          setShowAnnoLightbox({
            activeData_with_relatedNodeKeys,
            mode: "display",
            position: getAnnoLightboxPosition({ editor }),
          });
        },
      },
    });

  // Mirror the current selection to a ref so the readonly click listener can
  // read it synchronously when opening the picker.
  const selectionRef = useRef([]);
  selectionRef.current = selectedAnnoDataIDs;
  // The selection that was active when the picker opened — restored if the
  // picker is dismissed without picking. null when nothing was selected.
  const priorSelectionRef = useRef(null);

  const {
    setActiveAnnoDataIDs: setHoveredAnnoDataIDs,
    resetActiveAnnoDataIDs: resetHoveredAnnoDataIDs,
  } = useHighlightAnnoOnEvent({
    hover_select: "hover",
    classToApply: "-hovered-",
    markNodeMap,
    annoData,
    editor,
  });

  // ---- Annotation edit / remove handlers ----
  // These are the SAME functions the in-editor lightbox triggers — pulled out
  // of the lightbox JSX (bodies unchanged) so they can ALSO be triggered from
  // a host elsewhere on the page (an AnnoCard) via annoEditorRegistry below.
  // Philosophy: the action's call site may move, but the function it runs is
  // exactly what it always was.

  // Extracted verbatim from setEditMode's inline lock-acquire. Returns false
  // if another user holds the lock (no-op true when there's no collab context).
  const acquireEditLock = async (dataId) => {
    if (!docCollab) return true;
    const result = await docCollab.acquireLock(dataId);
    if (!result?.ok) return false;
    heldLockDataIdRef.current = dataId;
    return true;
  };

  // Identical to the original inline handleConfirmEdit. setShowAnnoLightbox(undefined)
  // is a harmless no-op when triggered from a host that doesn't use the
  // in-editor lightbox.
  const handleConfirmEdit = ({ data, dataId }) => {
    handleSetAnnoData({ type: "tags", data, dataId });
    releaseHeldLockIfAny();
    setShowAnnoLightbox(undefined);
  };

  // Identical body to the original inline handleRemove.
  const handleRemove = ({ nodeKeys, dataId: targetDataId }) => {
    // Signal "annotation mutation in flight" so OnChangePlugin's
    // Path 1 skips its partial-state fire during the commit.
    // setTimeout(0) (macrotask) rather than queueMicrotask, because
    // Lexical commits editor.update via its own microtask — a
    // microtask reset would fire BEFORE Lexical's commit microtask,
    // leaving the ref cleared by the time Path 1 reads it.
    isAnnoMutatingRef.current = true;
    setTimeout(() => {
      isAnnoMutatingRef.current = false;
    }, 0);
    editor.update(() => {
      /*
        these are all the mark nodes that need to be changed. by change we mean:
        1// either remove it completely IF this mark node has a single dataId behind it that is equal to the targetDataId
        2// else if this node has multiple dataIds behind it, then remove this dataId from the array of dataIds behind this node
        */
      const markNodesToChange = nodeKeys.map((key) => {
        const node = $getNodeByKey(key);
        return { nodeKey: key, node, dataIds: $getMarkIDs(node) };
      });
      const markNodesToUnwrap = markNodesToChange.filter(
        (d) => d.dataIds.length === 1 && d.dataIds[0] === targetDataId
      );
      const markNodesToEdit = markNodesToChange.filter(
        (d) => d.dataIds.length > 1 && d.dataIds.includes(targetDataId)
      );

      // console.log({markNodesToUnwrap, markNodesToEdit})

      markNodesToUnwrap.forEach(({ node }) => {
        $unwrapMarkNode(node);
      });

      markNodesToEdit.forEach(({ node, nodeKey }) => {
        node.deleteID(targetDataId);
        /**
         * .deleteId somehow does not trigger a related
         * 'updated' listen inside the mutationListener above.
         *
         * Hence here only we remove this dataId from the
         * markNodeMap. (typically this work is done in the mutation listener above)
         * Else strange bugs will occur
         */
        markNodeMap.delete(targetDataId);
      });
      setShowAnnoLightbox(undefined);
      // Pair with the close: when the unwrap triggers an annoData
      // change, useHighlightAnnoOnEvent re-runs and (if activeIds
      // still references the removed id) re-fires the select
      // callback, re-opening the lightbox at {0,0} because there's
      // no longer a DOM selection. resetActiveAnnoDataIDs prevents
      // that re-trigger.
      resetActiveAnnoDataIDs();
      // Release any lock we held for this annotation. Idempotent —
      // server checks holder before clearing.
      releaseHeldLockIfAny();
      if (docCollab) docCollab.releaseLock(targetDataId);
    });
  };

  // Register this editor's handlers into the page-level registry (if a host
  // mounted one), keyed by the editor instance. A sibling host (AnnoCard) can
  // then look up by annoDataId and trigger these exact handlers. The registered
  // handle reads from a ref updated every render, so it always runs current
  // logic despite registering only once.
  const annoEditorHandleRef = useRef({});
  annoEditorHandleRef.current = {
    hostsDataId: (id) => markNodeMap.has(id),
    getTags: (id) => latestAnnoDataRef.current?.[id]?.tags ?? {},
    annoConfig: annotation, // the field's annotation config (tagTypesConfig etc.)
    // The collab valuePath of the field hosting these annotations. A sidebar
    // host (AnnoCard) reads this to look up the field's lock state in the
    // page-level CollabEditContext and disable its edit/delete affordances
    // when another collaborator holds the field — mirroring the lock overlay
    // that already blocks the in-editor path.
    getValuePath: () => valuePath,
    // Editing an annotation's tags from a host OUTSIDE this editor (an
    // AnnoCard in the sidebar) must occupy THIS field's collab session, so the
    // lexical editor reads as "someone is editing" — same intent the legacy
    // per-annotation lock had. beginEdit engages the field only AFTER the lock
    // acquire succeeds: a caller (openAddTag) bails on !ok without calling
    // endEdit, so engaging pre-acquire would leak the field lock. setEngaged is
    // idempotent, so endEdit disengaging when we never engaged is a safe no-op.
    beginEdit: async (id) => {
      const ok = await acquireEditLock(id);
      if (ok) collabFieldEngagement?.setEngaged(true);
      return ok;
    },
    endEdit: () => {
      collabFieldEngagement?.setEngaged(false);
      return releaseHeldLockIfAny();
    },
    confirmEdit: ({ data, dataId }) => handleConfirmEdit({ data, dataId }),
    remove: (id) =>
      handleRemove({ nodeKeys: Array.from(markNodeMap.get(id) || []), dataId: id }),
    // Card→mark highlight: toggle the original `-selected-` class on every
    // mark fragment of this dataId, resolved via markNodeMap (handles
    // overlapping/fragmented marks). Imperative on purpose — a host (AnnoCard)
    // drives it through the registry, bypassing the memoized ViewBlockGenerator
    // that swallows reactive props.
    focusMark: (id, on) => {
      const keys = markNodeMap.get(id);
      if (!keys) return;
      for (const key of keys) {
        const el = editor.getElementByKey(key);
        if (el) el.classList.toggle("-selected-", !!on);
      }
    },
    // Filter dimming: replace the whole set of dimmed (filtered-out) dataIds in
    // one shot, then repaint ONCE. Driven imperatively by a host
    // (PublishedPageV3) through the registry whenever a filter changes. The host
    // computes the complete dimmed set and broadcasts it to every editor, so the
    // cost of a filter toggle is a single fragment sweep per editor rather than
    // one sweep per annotation (the old per-id dimMark was O(annos × fragments)
    // and painted transient partial states on the way to the final set).
    //
    // Unlike a flat grey wash, dimming is resolved PER FRAGMENT: a fragment
    // shared by an included annotation and an excluded one must read as the
    // included annotation's yellow mixed with the excluded one's grey, not as
    // a uniform grey (which would erase the still-relevant annotation) nor as
    // full-strength overlap yellow (which ignores the filter). So we only track
    // which ids are dimmed here and let repaintFilterHighlights recompute every
    // fragment's included/excluded split. The grey `color` (host's theme grey,
    // e.g. var(--grey-10)) is threaded through to CSS as --anno-dim-color; the
    // opacity + compositing live in CSS (themes/anno.js, -anno-filtered-). The
    // dimmed set is shared read-only across editors — repaint only reads it, and
    // each editor checks membership against the full set for its own fragments.
    setDimmedAnnos: (dimmedIds, color) => {
      dimmedIdsRef.current = dimmedIds instanceof Set ? dimmedIds : new Set(dimmedIds);
      if (color) dimColorRef.current = color;
      repaintFilterHighlights();
    },
  };

  const annoEditorRegistry = useAnnoEditorRegistry();
  useEffect(() => {
    if (!annoEditorRegistry) return undefined;
    return annoEditorRegistry.register(editor, {
      hostsDataId: (id) => annoEditorHandleRef.current.hostsDataId(id),
      getTags: (id) => annoEditorHandleRef.current.getTags(id),
      getAnnoConfig: () => annoEditorHandleRef.current.annoConfig,
      getValuePath: () => annoEditorHandleRef.current.getValuePath(),
      beginEdit: (id) => annoEditorHandleRef.current.beginEdit(id),
      endEdit: () => annoEditorHandleRef.current.endEdit(),
      confirmEdit: (args) => annoEditorHandleRef.current.confirmEdit(args),
      remove: (id) => annoEditorHandleRef.current.remove(id),
      focusMark: (id, on) => annoEditorHandleRef.current.focusMark(id, on),
      setDimmedAnnos: (dimmedIds, color) =>
        annoEditorHandleRef.current.setDimmedAnnos(dimmedIds, color),
    });
  }, [annoEditorRegistry, editor]);

  /**
   * ---- READONLY MODE (full) + V2 EDIT MODE (outside-click deselect only) ----
   * Detects clicks on <mark> elements via a document-level click listener.
   * In readOnly it replaces the cursor-based registerUpdateListener used in
   * edit mode, and listens on `document` so clicking anywhere outside the
   * annotation also un-blues it.
   *
   * In V2 EDIT mode the cursor-based registerUpdateListener still owns every
   * in-editor selection concern (mark click → onAnnotationClick(id)) — but it
   * only fires on Lexical selection changes, so it structurally cannot see
   * clicks outside the editor (host header, sidebar whitespace, AI panel).
   * This listener registers in that mode ONLY to report those clicks as a
   * deselect to the host; any mark click bails out untouched below.
   */
  useEffect(() => {
    if (!readOnly && !v2) return;

    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const handleClick = (e) => {
      // Clicks inside the lightbox portal (e.g. the ActionMenu's Edit/Clear
      // buttons) must not be treated as outside-mark clicks — the lightbox
      // owns its own close behaviour via AnnoLightboxShell's backdrop.
      if (e.target.closest('#annoLightbox-root')) {
        return;
      }

      const markElement = e.target.closest('mark');

      if (!readOnly) {
        // V2 EDIT mode: deselect-only. A click on ANY mark — this editor's
        // (the cursor path selects it) or a sibling editor's (its own
        // selection path does) — is a select, never an outside click, so it
        // must not race a deselect against that select: every field's plugin
        // has this same document listener and they all fire for one click.
        // Same skip-list as the readonly v2 branch below for anno UI that
        // sits/portals outside the marks.
        if (markElement) return;
        if (e.target.closest('[data-anno-id], .js-anno-keep-focus')) return;
        onAnnotationClick?.(null);
        return;
      }

      if (!markElement || !rootElement.contains(markElement)) {
        // V2 (host-owned selection, e.g. PublishedPageV3): the host owns the
        // focus state, so route the outside-click DESELECT back to it via
        // onAnnotationClick(null) — this is the same listener the non-V2 path
        // uses to clear focus, just reported to the host instead of cleared
        // internally. Skip clicks on annotation UI that portals/sits outside
        // the marks and sets/keeps the selection itself: the sidebar cards
        // (`[data-anno-id]`) and the anno pickers (`.js-anno-keep-focus`). The
        // in-editor lightbox is already excluded above via `#annoLightbox-root`.
        if (v2) {
          if (e.target.closest('[data-anno-id], .js-anno-keep-focus')) return;
          onAnnotationClick?.(null);
          return;
        }
        resetActiveAnnoDataIDs();
        setShowAnnoLightbox(undefined);
        return;
      }

      // Annotation click is its own action (open the lightbox). Don't let it
      // also trigger ancestor onClick handlers (e.g. DataPivot's row that
      // navigates to the source content).
      e.stopPropagation();

      // Reverse-lookup: find which annoDataIDs this <mark> element belongs to
      const annoDataIDs = [];
      for (const [id, keys] of markNodeMap) {
        for (const key of keys) {
          if (editor.getElementByKey(key) === markElement) {
            annoDataIDs.push(id);
            break;
          }
        }
      }

      if (annoDataIDs.length > 0) {
        // V2 + overlap: open the picker directly, WITHOUT routing through
        // setActiveAnnoDataIDs — that paints -selected- on every overlapping
        // mark. The picker leaves the document marks untouched (no select or
        // hover styles); the host re-highlights only the chosen one on pick.
        if (v2 && annoDataIDs.length > 1) {
          const items = editor.getEditorState().read(() =>
            annoDataIDs.map((id) => {
              const keys = markNodeMap.get(id);
              const nodeKeys = keys ? Array.from(keys) : [];
              const textFragment = nodeKeys
                .map((k) => $getNodeByKey(k)?.getTextContent() ?? "")
                .join("");
              return {
                dataId: id,
                excerpt: textFragment,
                // Rich quote (serialized lexical value) for original formatting —
                // same source AnnoCard uses; latestAnnoDataRef is always current.
                fragment: latestAnnoDataRef.current?.[id]?.fragment,
              };
            })
          );
          // Rank by specificity — shortest annotated span first.
          items.sort(
            (a, b) => (a.excerpt?.length ?? Infinity) - (b.excerpt?.length ?? Infinity)
          );
          // Remember the current selection so it can be restored if the picker
          // is dismissed without picking, then clear it so the picker opens over
          // clean (unstyled) marks.
          priorSelectionRef.current = selectionRef.current;
          resetActiveAnnoDataIDs();
          resetHoveredAnnoDataIDs();
          setOverlapPicker({ anchorEl: markElement, items });
          return;
        }
        setActiveAnnoDataIDs(annoDataIDs);
      } else {
        resetActiveAnnoDataIDs();
      }
    };

    // Capture phase so this fires before any ancestor's bubble-phase onClick
    // (e.g. DataPivot row navigation), letting the stopPropagation above
    // actually halt the click before it reaches them.
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [readOnly, editor, markNodeMap]);

  // Card→mark highlight: apply the original `-selected-` class to every mark
  // whose annoDataId is in activeAnnoIds. Driven by host selection state
  // (e.g. PublishedPage's selectedAnnoId), using the same markNodeMap +
  // `-selected-` class as the in-editor click highlight — no dimming. This is
  // the card→mark counterpart of the mark→card onAnnotationClick bridge.
  useEffect(() => {
    const active = new Set(activeAnnoIds || []);
    if (active.size === 0) return undefined;
    const applied = [];
    for (const [id, keys] of markNodeMap) {
      if (!active.has(id)) continue;
      for (const key of keys) {
        const el = editor.getElementByKey(key);
        if (el) {
          el.classList.add("-selected-");
          applied.push(el);
        }
      }
    }
    return () => {
      applied.forEach((el) => el.classList.remove("-selected-"));
    };
  }, [activeAnnoIds, editor, markNodeMap]);

  useEffect(() => {
    const markNodeKeysToIDs = new Map();
    return mergeRegister(
      /**
       * by default lexical stores overlapping MarkNodes
       * data in a very shitty way. This utility resolves that,
       * making the data structure a nice and clean:
       * [
       *   {ids: [A], ...},
       *   {ids: [A, B], ...},
       *   {ids: [B], ...},
       * ]
       */
      registerNestedElementResolver(
        editor,
        MarkNode,
        (from) => {
          return $createMarkNode(from.getIDs());
        },
        (from, to) => {
          // Merge the IDs
          const ids = from.getIDs();
          ids.forEach((id) => {
            to.addID(id);
          });
        }
      ),
      /**
       * ---- BOTH MODES ----
       * Creates a map of MarkNode ids to all the nodekeys that have this id.
       * This is in a sense a better representation of 'which are all the nodes
       * that have the same data attached to it'. This way, in the case of overlaps, we can
       * keep track of which pieces belong to one complete anno string (in the case of overlaps).
       * Also applies color classes and registers mouseover/mouseout for hover highlights.
       * Looks something like:
       * {
       *  A: [key1, key2]
       *  B: [key3, key3]
       * }
       */
      editor.registerMutationListener(MarkNode, (mutations, { updateTags } = {}) => {
        const registeredElements = new WeakSet();
        editor.getEditorState().read(() => {
          for (const [key, mutation] of mutations) {
            const node = $getNodeByKey(key);
            let ids = [];

            if (mutation === "destroyed") {
              ids = markNodeKeysToIDs.get(key) || [];
            } else if ($isMarkNode(node)) {
              ids = node.getIDs();
            }

            for (let i = 0; i < ids.length; i++) {
              const id = ids[i];
              let markNodeKeys = markNodeMap.get(id);
              markNodeKeysToIDs.set(key, ids);

              if (mutation === "destroyed") {
                if (markNodeKeys !== undefined) {
                  markNodeKeys.delete(key);
                  if (markNodeKeys.size === 0) {
                    markNodeMap.delete(id);
                  }
                }
              } else {
                if (markNodeKeys === undefined) {
                  markNodeKeys = new Set();
                  markNodeMap.set(id, markNodeKeys);
                }
                if (!markNodeKeys.has(key)) {
                  markNodeKeys.add(key);
                }
              }
            }

            const element = editor.getElementByKey(key);

            applyClassesForAnnoColor({
              element,
              latestAnnoDataRef,
              ids,
              node
            })

            if (
              // Updated might be a move, so that might mean a new DOM element
              // is created. In this case, we need to add an event listener too.
              (mutation === "created" || mutation === "updated") &&
              $isMarkNode(node) &&
              element !== null &&
              !registeredElements.has(element)
            ) {
              registeredElements.add(element);
                
              for (let i = 0; i < ids.length; i++){
                element.id = `${ids[i]}`;
              }

              element.addEventListener("mouseover", (e) => {
                // No hover styling on the document marks while the overlap
                // picker is open.
                if (overlapPickerOpenRef.current) return;
                if (ids?.length > 0) {
                  // On an overlap, highlight only the most-specific annotation
                  // (shortest span) rather than every overlapping one.
                  const winner = getMostSpecificAnnoId(ids);
                  setHoveredAnnoDataIDs(winner ? [winner] : ids);
                }
              });
              element.addEventListener("mouseout", (e) =>
                resetHoveredAnnoDataIDs((prev) =>
                  prev.length === 0 ? prev : []
                )
              );
            }
          }

          // Skip on `history-merge` commits — that's the tag Lexical attaches
          // to internal housekeeping updates (e.g. the `markAllNodesAsDirty`
          // pass that `registerNodeTransform` fires when this plugin mounts,
          // marking every existing MarkNode dirty so the new transform sees
          // them). The mutation listener still fires for all of those nodes
          // with mutation='updated', but no real user edit happened. Without
          // this skip, recomputing fragments would flip the annoData reference
          // and trip OnChangePlugin's Path 2 useEffect on every page load that
          // has annotations — firing one spurious quickUpdateContent save per
          // annotated block, which collide on the same doc and cause Mongo
          // WriteConflict (code 112).
          if (updateTags?.has('history-merge')) return;

          //set the annoData state which in turn
          //triggers the parentOnChange of LexicalTextEditor
          handleUpdateFragmentsInAnnoData(
            constructAndInjectLexicalValueForEachAnnoDataId(
              Object.fromEntries(markNodeMap), // convert marknodemap into serialized editorstate jsons and save to annodata
            )
          );
        });
      }), // END registerMutationListener on MarkNode

      /**
       * ---- EDIT MODE ONLY ----
       * Depending on where the cursor currently is,
       * it figures out what is the MarkNode under it,
       * and what markIDs does it have. With this info,
       * we can now highlight all the MarkNodes that have
       * these markIDs.
       * Skipped entirely in readOnly — the document click listener handles it instead.
       */
      ...(readOnly ? [] : [
        editor.registerUpdateListener(({ editorState, tags }) => {
          editorState.read(() => {
            const selection = $getSelection();
            let hasActiveIds = false;

            if ($isRangeSelection(selection)) {
              const anchorNode = selection.anchor.getNode();

              if ($isTextNode(anchorNode)) {
                const annoDataIDs = $getMarkIDs(
                  anchorNode,
                  selection.anchor.offset
                );
                if (annoDataIDs !== null) {
                  setActiveAnnoDataIDs(annoDataIDs);
                  hasActiveIds = true;
                }
              }
            }
            if (!hasActiveIds) {
              resetActiveAnnoDataIDs();
            }
          });
        }),
      ]),

      // ---- EDIT MODE ONLY ---- dispatched by Anno button in toolbar: ../ToolbarPlugin/AnnoTool/index.js
      editor.registerCommand(
        SHOW_ANNO_LIGHTBOX_COMMAND,
        () => {
          // Snapshot the live selection before focus shifts to the lightbox.
          createTargetSelectionRef.current = editor
            .getEditorState()
            .read(() => {
              const sel = $getSelection();
              return sel ? sel.clone() : null;
            });
          setShowAnnoLightbox({
            position: getAnnoLightboxPosition({ editor }),
          });

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      )
    );
  }, [editor, markNodeMap]);

  return (
    <>
      {overlapPicker && (
        <AnnoOverlapPicker
          variant={getOverlapPickerVariant()}
          anchorEl={overlapPicker.anchorEl}
          items={overlapPicker.items}
          // Hovering a row paints the matching mark's hover style. Driven
          // programmatically (not via the mark's own mouseover, which is gated
          // while the picker is open), so it works through the picker overlay.
          onHoverItem={(dataId) => {
            if (dataId) setHoveredAnnoDataIDs([dataId]);
            else resetHoveredAnnoDataIDs();
          }}
          onPick={(dataId) => {
            // Picked → the chosen annotation becomes the selection (transcript
            // mark + sidebar card). Don't restore the prior selection.
            priorSelectionRef.current = null;
            setOverlapPicker(undefined);
            resetHoveredAnnoDataIDs();
            if (dataId) {
              setActiveAnnoDataIDs([dataId]);
              if (onAnnotationClick) onAnnotationClick(dataId);
            } else {
              resetActiveAnnoDataIDs();
            }
          }}
          onClose={() => {
            // Dismissed without picking → restore the selection that was active
            // before the picker opened (only if there was one; otherwise leave
            // nothing selected).
            setOverlapPicker(undefined);
            resetHoveredAnnoDataIDs();
            const prior = priorSelectionRef.current;
            priorSelectionRef.current = null;
            if (prior && prior.length) setActiveAnnoDataIDs(prior);
            else resetActiveAnnoDataIDs();
          }}
        />
      )}
      {showAnnoLightbox && (
        <AnnoLightbox
          data={showAnnoLightbox.data}
          setData={({ tagType, value }) => {
            setShowAnnoLightbox((prev) => {
              const newData = prev.data ? cloneDeep(prev.data) : {};
              if (value?.data.length) newData[tagType] = value;
              else delete newData[tagType];
              return {
                ...prev,
                data: newData,
              };
            });
          }}
          nodeKeys={showAnnoLightbox.nodeKeys}
          dataId={showAnnoLightbox.dataId}
          mode={showAnnoLightbox.mode}
          activeData_with_relatedNodeKeys={
            showAnnoLightbox.activeData_with_relatedNodeKeys
          } //only required if mode = display
          setEditMode={async ({ data, dataId, nodeKeys }) => {
            // If a collab pipeline is in play, try to acquire the lock
            // before letting the lightbox switch to edit mode. If another
            // user grabbed it in the same instant, bail out — the UI layer
            // already gates on isLockedByOther but a true race can slip
            // through.
            const ok = await acquireEditLock(dataId);
            if (!ok) {
              setShowAnnoLightbox(undefined);
              resetActiveAnnoDataIDs();
              return;
            }
            setShowAnnoLightbox((prev) => ({
              ...prev,
              mode: "edit",
              data,
              dataId,
              nodeKeys,
            }));
          }}
          position={showAnnoLightbox.position}
          handleClickOutside={() => {
            releaseHeldLockIfAny();
            setShowAnnoLightbox(undefined);
            resetActiveAnnoDataIDs();
          }}
          annoConfig={annotation}
          readOnly={readOnly}
          allowEdit={allowAnnotationEditWhileReadOnly}
          handleCancel={() => {
            releaseHeldLockIfAny();
            setShowAnnoLightbox(undefined);
          }}
          handleConfirmEdit={handleConfirmEdit}
          handleRemove={handleRemove}
          handleConfirmCreate={
            // targetSelection was captured when the lightbox opened (see
            // createTargetSelectionRef above) — falls back to live selection
            // inside handleCreateAnnotation if null.
            ({ data }) => {
              handleCreateAnnotation({
                data,
                targetSelection: createTargetSelectionRef.current,
                editor,
                handleSetAnnoData,
                callback: () => setShowAnnoLightbox(undefined),
              });
            }

            // insertAnno(
            //   {
            //     value: { tags: data.data },
            //     editorState,
            //     onEditorChange: (k, v) => handleChange(v),
            //   },
            //   () => setShowAnnoLightbox(undefined)
            // )
          }
        />
      )}
    </>
  );
};
